import React, { useEffect, useState } from "react";
import SocTile from "./SocTile";
import SocIncidentQueue from "./SocIncidentQueue";
import SocTrendsSection from "./SocTrendsSection";
import SocVulnerabilitiesSection from "./SocTopVulnerabilities";
import SocExternalEvents, { EXTERNAL_EVENT_BANDS } from "./SocExternalEvents";
import { scrollToId } from "./socUi";
import { SocSeverityDonut } from "./SocOverviewCharts";
import { SocConnectorsPanel, SocDataChecks, SocStaleBanner } from "./SocFreshnessPanels";
import { SOC_TILES } from "./socTileConfig";

const isConnected = (tile) => !!tile && tile.status !== "notConnected" && tile.connected !== false;
const hasKey = (tile, key) => Object.prototype.hasOwnProperty.call(tile?.metrics || {}, key);

// The tile a view shows. A sub-area of a tile (Dark web, Code & DevOps under External threat) shows
// only its own metrics and links to its own screen; when none of its metrics came back, its source
// is not connected even though the parent tile is.
// A CVE view of an org on the new CVE pipeline before its first snapshot is connected but has no figures yet (the summary
// sends none rather than zeros): its tile says so instead of "not connected".
const viewTile = (view, tiles, vulnerabilities = null) => {
  const tile = tiles.find((t) => t.key === view.tile);
  const base = SOC_TILES[view.tile];
  if (!view.metricKeys) {
    return { tile, config: view.link ? { ...base, link: () => view.link } : base };
  }
  const present = view.metricKeys.some((k) => hasKey(tile, k));
  const cvesStarting = view.cves && vulnerabilities?.generation && !vulnerabilities.generation.hasFigures;
  const absent = cvesStarting
    ? { ...tile, key: view.key, metrics: {}, pending: view.metricKeys }
    : { ...tile, key: view.key, status: "notConnected", connected: false, metrics: {} };
  return {
    tile: present ? tile : absent,
    config: {
      ...base,
      title: view.title || base.title,
      metrics: base.metrics.filter(([key]) => view.metricKeys.includes(key)),
      link: view.link ? () => view.link : base.link,
      caption: null,
      // The parent tile's headline would count the other sub-areas too.
      headline: false,
    },
  };
};

// One Security Operations menu view other than the Overview (/soc?view=key): the area's tile, its
// trend charts and its part of the incident queue - the same data and components as the Overview,
// narrowed to the area.
// The queue source each "Open now" External threat figure stands for.
export const OPEN_RISK_SOURCES = {
  openEsmRisks: "Attack surface",
  openSecretsRisks: "Exposed secret",
  openLeakedCredentials: "Leaked credential",
  openRansomwareRisks: "Ransomware",
};

const SocAreaView = ({
  view, tiles, vulnerabilities = null, linkAccess = null, now, canConnect, days, onDaysChange, onTrends, reloadKey, pollKey = 0,
  queue, userId, canManage, initialMetric = null,
}) => {
  // The tile figure opened (External threat): an event figure lists its events under the tile, an
  // "Open now" figure filters the queue to its source. Hooks first: the connectors view returns early.
  const [selected, setSelected] = useState(initialMetric);
  useEffect(() => { setSelected(initialMetric); }, [view.key, initialMetric]);
  const queueSource = OPEN_RISK_SOURCES[selected] || null;
  const eventBand = EXTERNAL_EVENT_BANDS[selected] ? selected : null;
  useEffect(() => {
    if (!selected) return undefined;
    const t = setTimeout(() => scrollToId(queueSource ? "soc-queue" : "soc-external-events"), 0);
    return () => clearTimeout(t);
  }, [selected, queueSource]);
  const onMetric = (key) => setSelected((cur) => (cur === key ? null : key));

  if (view.key === "connectors") {
    return (
      <>
        <h2 className="mb-3 text-xl font-semibold text-content-primary">Data connectors</h2>
        <SocStaleBanner tiles={tiles} now={now} canConnect={canConnect} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <SocConnectorsPanel tiles={tiles} now={now} />
          <SocDataChecks tiles={tiles} now={now} />
        </div>
      </>
    );
  }

  const { tile, config } = viewTile(view, tiles, vulnerabilities);
  const incidents = view.key === "incidents" && isConnected(tile) ? tile : null;
  const chartSpan = incidents ? "" : "lg:col-span-2";

  return (
    <div data-testid={`soc-view-${view.key}`}>
      <h2 className="mb-3 text-xl font-semibold text-content-primary">{view.title || view.label}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-2">
        <SocTile tile={tile} config={config} canConnect={canConnect} now={now} linkAccess={linkAccess}
          onMetric={config.clickable ? onMetric : null} activeMetric={selected} />
        {incidents && <SocSeverityDonut tile={incidents} />}
        {view.charts && isConnected(tile) && (
          <div className={`min-w-0 ${chartSpan}`}>
            <SocTrendsSection
              tiles={tiles}
              days={days}
              onDaysChange={onDaysChange}
              onTrends={onTrends}
              reloadKey={reloadKey}
              pollKey={pollKey}
              charts={view.charts}
              findings={view.findings}
              bandKeys={view.bandKeys}
              areaKey={view.tile}
            />
          </div>
        )}
      </div>
      {eventBand && (
        <div className="mb-4">
          <SocExternalEvents band={eventBand} onClose={() => setSelected(null)} />
        </div>
      )}
      {view.cves && (
        // The same component and summary data as the Overview, so the two never disagree.
        <SocVulnerabilitiesSection
          vulnerabilities={vulnerabilities}
          endpointTile={tiles.find((t) => t.key === "endpoint")}
          now={now}
          riskDetailsKey={queue?.changeKey}
        />
      )}
      {!view.queue && view.note && (
        <p className="mb-6 rounded-md bg-theme-secondary px-3 py-2 text-xs text-content-secondary" data-testid="soc-view-note">
          {view.note}
        </p>
      )}
      {view.queue && (
        <div className="mb-6">
          <SocIncidentQueue
            queue={queue}
            area={view.queue.area || null}
            source={queueSource || view.queue.source || null}
            onClearSource={queueSource ? () => setSelected(null) : undefined}
            userId={userId}
            canManage={canManage}
          />
        </div>
      )}
    </div>
  );
};

export default SocAreaView;
